import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import { alpha, useTheme } from '@mui/material';
import { InterrogationMemory, OntologyEntity } from '../utils/backend-utils';
import { categoryColor, ENTITY_CATEGORIES } from '../utils/ontologyConstants';
import { displaySpeakerName } from './InterrogationTranscriptView';

interface InterrogationExtractionSidebarProps {
  memory?: InterrogationMemory;
  speakerRegistry?: Record<string, string>;
  speakerLabels?: string[];
  openThreadsCount?: number;
  onSpeakerRegistryChange?: (registry: Record<string, string>) => void;
  isExtracting?: boolean;
}

function getEntities(memory?: InterrogationMemory): OntologyEntity[] {
  if (memory?.entities && memory.entities.length > 0) {
    return memory.entities;
  }
  return (memory?.entity_registry || []).map((row) => ({
    id: row.name,
    name: row.name,
    category: 'Other',
    subtype: row.type,
    notes: row.notes,
  }));
}

export function InterrogationExtractionSidebar({
  memory,
  speakerRegistry = {},
  speakerLabels = [],
  openThreadsCount = 0,
  onSpeakerRegistryChange,
  isExtracting = false,
}: InterrogationExtractionSidebarProps) {
  const theme = useTheme();
  const entities = getEntities(memory);
  const openThreads = memory?.open_threads || [];
  const relationCount = memory?.relations?.length || 0;
  const [localRegistry, setLocalRegistry] = useState<Record<string, string>>(speakerRegistry);

  useEffect(() => {
    setLocalRegistry(speakerRegistry);
  }, [speakerRegistry]);

  const labelsToShow = Array.from(
    new Set([
      ...Object.keys(speakerRegistry),
      ...speakerLabels.filter((label) => label && label !== 'unknown'),
    ])
  ).sort();

  const commitSpeakerName = (label: string, displayName: string) => {
    const trimmed = displayName.trim();
    const next = { ...localRegistry };
    if (trimmed) {
      next[label] = trimmed;
    } else {
      delete next[label];
    }
    setLocalRegistry(next);
    onSpeakerRegistryChange?.(next);
  };

  const entitiesByCategory = ENTITY_CATEGORIES.reduce<Record<string, OntologyEntity[]>>((acc, cat) => {
    const items = entities.filter((e) => (e.category || 'Other') === cat);
    if (items.length > 0) acc[cat] = items;
    return acc;
  }, {});

  return (
    <Box
      sx={{
        width: 320,
        flexShrink: 0,
        borderLeft: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        bgcolor: alpha(theme.palette.background.paper, 0.6),
        overflowY: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Typography variant="subtitle2" fontWeight={700}>
        Extracted knowledge
      </Typography>

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Rolling summary
        </Typography>
        <Typography variant="body2">
          {memory?.rolling_summary?.trim() || 'Listening for workflow details…'}
        </Typography>
      </Box>

      <Divider />

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Open threads ({openThreadsCount || openThreads.length})
        </Typography>
        {openThreads.length === 0 ? (
          <Typography variant="body2" color="text.secondary">None yet</Typography>
        ) : (
          openThreads.slice(0, 8).map((thread) => (
            <Typography key={thread} variant="body2" sx={{ mb: 0.5 }}>
              • {thread}
            </Typography>
          ))
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Entities ({entities.length}) · Relations ({relationCount})
        </Typography>
        {entities.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {isExtracting ? 'Extracting from your message…' : 'None yet — paste text and press Enter or Send'}
          </Typography>
        ) : (
          Object.entries(entitiesByCategory).map(([cat, items]) => (
            <Box key={cat} sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ color: categoryColor(cat), fontWeight: 600 }}>
                {cat}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                {items.slice(0, 6).map((entity) => (
                  <Chip
                    key={entity.id}
                    size="small"
                    label={entity.subtype ? `${entity.name} (${entity.subtype})` : entity.name}
                    variant="outlined"
                    sx={{ borderColor: alpha(categoryColor(cat), 0.5) }}
                  />
                ))}
              </Box>
            </Box>
          ))
        )}
      </Box>

      {labelsToShow.length > 0 && (
        <>
          <Divider />
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Speakers
            </Typography>
            {labelsToShow.map((label) => (
              <Box key={label} sx={{ mb: 1.25 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                  {displaySpeakerName(label, speakerRegistry)}
                  <Box component="span" sx={{ color: 'text.disabled', ml: 0.5 }}>
                    ({label})
                  </Box>
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Display name"
                  value={localRegistry[label] || ''}
                  onChange={(event) => {
                    const value = event.target.value;
                    setLocalRegistry((prev) => ({ ...prev, [label]: value }));
                  }}
                  onBlur={(event) => commitSpeakerName(label, event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      commitSpeakerName(label, (event.target as HTMLInputElement).value);
                    }
                  }}
                  sx={{ '& .MuiInputBase-root': { fontSize: '0.875rem' } }}
                />
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
